import React, { useEffect, useMemo, useState } from "react";
import DownloadOverlay from "./DownloadOverlay";
/**
 * MyProjects Tab (Product sourced from metadata.json)
 * --------------------------------------------------------------
 * - Project directories on disk are:  output-<nameNoExt>
 * - We load every directory the backend returns (ideally all output-*).
 * - Product type is read from: /outputs/<user>/<folder>/metadata.json → { "Product": "<type>" }
 * - Thumbnail: thumbnail.png (only; no GIF support)
 * - Download button now POSTs to /download/ with JSON { email, name }
 */

const PAGE_SIZE = 10;
const KINDS = [
  { value: "all", label: "All types" },
  { value: "sign", label: "3D Sign" },
  { value: "keychain", label: "Keychain" },
  { value: "coaster", label: "Coaster" },
  { value: "stand", label: "Phone Stand" },
  { value: "lightbox", label: "Lightbox" },
  { value: "organizer", label: "Desk Organizer" },
  { value: "bookmark", label: "Bookmark" },
  { value: "cake topper", label: "Cake Topper" },
  { value: "standee", label: "Standee" },
  { value: "custom", label: "Custom Order Requests" },
  { value: "other", label: "Other" },
];

const SORTS = [
  { value: "updated_desc", label: "Last updated" },
  { value: "created_desc", label: "Newest" },
  { value: "created_asc", label: "Oldest" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
  { value: "size_desc", label: "Largest" },
  { value: "size_asc", label: "Smallest" },
];

const PRODUCT_COLORS = {
  sign: "#FF710A",     // Light Blue
  keychain: "#14b8a6", // Turquoise
  coaster: "#a855f7",  // Purple
  lightbox: "#db2777", // Magenta
  "desk organizer": "#0011ffff", // Blue
  bookmark: "#ff0000ff", // Red
  "cake topper": "#fad324ff", // Yellow
};

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function defaultAPI(username) {
  return {
    async listProjects({ q, page, pageSize, sort, kind }) {
      if (!username) return { items: [], total: 0 };
      const params = new URLSearchParams({
        user: username,
        page: String(page),
        page_size: String(pageSize),
        sort,
      });
      if (q) params.set("q", q);

      // ⬇️ Don't send "custom" to the backend – we'll filter it on the frontend
      if (kind && kind !== "all" && kind !== "custom") {
        params.set("kind", kind);
      }

      const res = await fetch(`/my-projects?${params.toString()}`);
      if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
      return res.json();
    },
    // deleteProject kept for compatibility (not used by handleDelete now)
    async deleteProject(id) {
      if (!username) throw new Error("No username");
      const res = await fetch(
        `/my-projects/${encodeURIComponent(id)}?user=${encodeURIComponent(username)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      return res.json();
    },
  };
}


function ActionButton({ label, onClick, title, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      style={{
        padding: "6px 10px",
        fontSize: 13,
        borderRadius: 6,
        border: "1px solid #cbd5e1",
        background: disabled ? "#e5e7eb" : "#ffffff",
        color: disabled ? "#9ca3af" : "#111827",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {label}
    </button>
  );
}

// "sign-BTTF" -> "output-BTTF"; "BTTF" -> "output-BTTF"; "output-BTTF" -> itself
function toOutputFolderFromId(id) {
  if (!id) return null;
  if (id.startsWith("output-")) return id;
  const parts = id.split("-");
  if (parts.length >= 2) return `output-${parts.slice(1).join("-")}`;
  return `output-${id}`;
}

function withVersion(url, ver) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    if (ver != null) u.searchParams.set("v", String(ver));
    return u.pathname + u.search + u.hash;
  } catch {
    return (
      url +
      (url.includes("?") ? "&" : "?") +
      "v=" +
      encodeURIComponent(String(ver ?? ""))
    );
  }
}

async function probeVersion(urls) {
  for (const url of urls) {
    if (!url) continue;
    try {
      const r = await fetch(url, { method: "HEAD", cache: "no-store" });
      if (!r.ok) continue;
      const lm = r.headers.get("last-modified");
      const etag = r.headers.get("etag");
      const len = r.headers.get("content-length");
      const ver = lm || etag || len;
      if (ver) return { url, ver };
      return { url, ver: "" };
    } catch {}
  }
  return null;
}

function stripTimestampTag(str) {
  if (!str) return str;
  return str.replace(/-(\d{8}[_-]\d{6})(?=\.[^.]+$|$)/, "");
}

function ProjectCard({ project, onOpen, onDelete, onReview, refreshKey }) {
  const [imgError, setImgError] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [productFromMeta, setProductFromMeta] = useState(
    project.productName || "Project"
  );
  const [resolvedFolder, setResolvedFolder] = useState(null); // e.g., "output-BTTF"
  const [version, setVersion] = useState("");
  const [isCheckingZip, setIsCheckingZip] = useState(false);

  const isCustom = !!project.isCustom;

    const fromLabel = useMemo(() => {
    return (
      project.customerEmail ||
      project.customer_email ||
      project.customerName ||
      project.customer_name ||
      null
    );
  }, [project]);

  const productColor = useMemo(() => {
    const key = (productFromMeta || "").toString().trim().toLowerCase();
    return PRODUCT_COLORS[key] || "#FF710A"; // fallback orange for unknown types
  }, [productFromMeta]);

  // Resolve filesystem userDir from previewUrl if possible
  const userDir = useMemo(() => {
    if (project.previewUrl) {
      const m = project.previewUrl.match(/^\/outputs\/([^/]+)\//);
      if (m) return m[1]; // e.g. "erez" or "erez:customer"
    }
    const u = project.user || "";
    return u.includes("@") ? u.split("@")[0] : u;
  }, [project.previewUrl, project.user]);

  // Prefer "output-<slug>" then "output-<fromId>" then raw id
  const candidateFolders = useMemo(() => {
    const c = [];
    if (project?.slug) c.push(`output-${project.slug}`);
    const fromId = toOutputFolderFromId(project?.id);
    if (fromId) c.push(fromId);
    if (project?.id) c.push(project.id);
    return Array.from(new Set(c));
  }, [project?.slug, project?.id]);

  // Resolve actual folder by probing
  useEffect(() => {
    if (!userDir || candidateFolders.length === 0) return;
    let cancelled = false;

    const headOk = async (url) => {
      try {
        const r = await fetch(url, { method: "HEAD", cache: "no-store" });
        return r.ok;
      } catch {
        return false;
      }
    };

    (async () => {
      for (const folder of candidateFolders) {
        const base = `/outputs/${userDir}/${folder}`;
        if (
          (await headOk(`${base}/thumbnail.png`)) ||
          (await headOk(`${base}/metadata.json`))
        ) {
          if (!cancelled) setResolvedFolder(folder);
          return;
        }
      }
      if (!cancelled) setResolvedFolder(candidateFolders[0]);
    })();

    return () => {
      cancelled = true;
    };
  }, [userDir, candidateFolders]);

  const rawPngUrl = useMemo(() => {
    if (project.previewUrl && /\.png(\?.*)?$/i.test(project.previewUrl)) {
      return project.previewUrl;
    }
    if (!userDir || !resolvedFolder) return null;
    return `/outputs/${userDir}/${resolvedFolder}/thumbnail.png`;
  }, [project.previewUrl, userDir, resolvedFolder]);

  useEffect(() => {
    let cancelled = false;
    if (!rawPngUrl) return;

    (async () => {
      const probed = await probeVersion([rawPngUrl]);
      if (cancelled) return;

      if (probed) {
        setVersion(probed.ver ?? "");
        setImgSrc(withVersion(probed.url, probed.ver ?? ""));
        setImgError(false);
      } else {
        setVersion("");
        setImgSrc(withVersion(rawPngUrl, ""));
        setImgError(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rawPngUrl, refreshKey]);

  const handleImgError = () => {
    setImgError(true);
    setImgSrc(null);
  };

  // Read Product from metadata.json (for both primary and custom roots)
  useEffect(() => {
    if (!userDir || !resolvedFolder) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/outputs/${userDir}/${resolvedFolder}/metadata.json`,
          { cache: "no-store" }
        );
        if (!res.ok) return;
        const data = await res.json();
        const prod = (data?.Product || data?.product || "").toString().trim();
        if (!cancelled && prod) setProductFromMeta(prod);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [userDir, resolvedFolder, refreshKey]);

  const displayTitleRaw = useMemo(() => {
    if (project.slug) return project.slug;
    if (project.name) return project.name;
    if (resolvedFolder?.startsWith("output-"))
      return resolvedFolder.slice("output-".length);
    return project.id || "project";
  }, [project.slug, project.name, project.id, resolvedFolder]);

  const displayTitle = useMemo(
    () => stripTimestampTag(displayTitleRaw),
    [displayTitleRaw]
  );

  // --- Download via POST /download/ { email, name } ---
  const handleDownload = async () => {
    if (!project?.user || !resolvedFolder) {
      alert("Missing project info for download.");
      return;
    }
    setIsCheckingZip(true);
    try {
      const res = await fetch("/download/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: project.user, // full email; backend uses .split('@')[0]
          name: resolvedFolder, // let backend normalize to slug + folder
        }),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Download failed (${res.status})`);
      }

      // Get filename from Content-Disposition if present
      const disp =
        res.headers.get("Content-Disposition") ||
        res.headers.get("content-disposition") ||
        "";
      let filename =
        (disp.match(/filename\*=UTF-8''([^;]+)/)?.[1] &&
          decodeURIComponent(
            disp.match(/filename\*=UTF-8''([^;]+)/)[1]
          )) ||
        disp.match(/filename="?([^"]+)"?/)?.[1] ||
        `${displayTitle || "project"}.zip`;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || "Download is not available for this project yet.");
    } finally {
      setIsCheckingZip(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        background: "#ffffff",
        boxShadow: "0 1px 2px rgba(219, 117, 21, 0.04)",
      }}
    >
      {/* Square preview */}
      <div
        style={{
          position: "relative",
          background: "#f8fafc",
          aspectRatio: "1 / 1",
        }}
      >
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={displayTitle}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={handleImgError}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            No <code>thumbnail.png</code>
          </div>
        )}

        {/* Product tags (top-left) */}
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              padding: "1px 8px",
              borderRadius: 999,
              fontSize: 12,
              background: productColor,
              color: "white",
              opacity: 0.95,
              textTransform: "capitalize",
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {productFromMeta}
          </span>
          {isCustom && (
            <span
              style={{
                padding: "1px 8px",
                borderRadius: 999,
                fontSize: 12,
                background: "#16a34a",
                color: "white",
                opacity: 0.95,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              Custom Request
            </span>
          )}
        </div>

        {/* "From: ..." pill (bottom-left) for any project that has customer info */}
        {fromLabel && (
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              background: "rgba(99, 99, 99, 0.85)",
              color: "white",
              padding: "3px 8px",
              borderRadius: 999,
              fontSize: 11,
              whiteSpace: "nowrap",
              maxWidth: "90%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`From: ${fromLabel}`}
          >
            From: {fromLabel}
          </div>
        )}

      </div>


      {/* Title + actions */}
      <div
        style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={displayTitle}
        >
          {displayTitle}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <ActionButton
            label={isCustom ? "Review" : "Edit"}
            onClick={() =>
              isCustom ? onReview?.(project) : onOpen(project)
            }
            title={
              isCustom ? "Review custom request" : "Open in designer"
            }
          />
          <ActionButton
            label="Delete"
            onClick={() => onDelete(project)}
          />
          {/* Download hidden for custom requests for now */}
          {!isCustom && (
            <ActionButton
              label={isCheckingZip ? "Downloading…" : "Download"}
              onClick={handleDownload}
              title="Download ZIP"
              disabled={!project?.user || !resolvedFolder || isCheckingZip}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyProjects({ username, onOpenProject, api: apiProp, onCreditsUpdate }) {

  const api = useMemo(() => apiProp || defaultAPI(username), [apiProp, username]);

  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [page, setPage] = useState(1);
  const debouncedQ = useDebounced(q, 350);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // overlay for reviewing custom requests
  const [reviewProject, setReviewProject] = useState(null);
  const [showCustomCreditOverlay, setShowCustomCreditOverlay] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.listProjects({
          q: debouncedQ,
          page,
          pageSize: PAGE_SIZE,
          sort,
          kind,
        });
        if (!cancelled) {
          setItems(res.items || []);
          setTotal(res.total || 0);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load projects");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, debouncedQ, page, sort, kind, refreshKey, username]);

  async function handleDelete(project) {
    const nameForPrompt = stripTimestampTag(
      project.slug || project.name || project.id
    );
    if (
      !window.confirm(
        `Delete project "${nameForPrompt}"? This cannot be undone.`
      )
    )
      return;

    const prev = items;
    setItems(prev.filter((p) => p.id !== project.id));

    try {
      // Decide which "user" to send to the backend for deletion
      let userForDelete = username;

      // For custom projects, use the filesystem root from previewUrl:
      //   /outputs/<fsRoot>/output-.../thumbnail.png  → fsRoot = "username:customer"
      if (project.isCustom && project.previewUrl) {
        const m = project.previewUrl.match(/^\/outputs\/([^/]+)\//);
        if (m && m[1]) {
          userForDelete = m[1];
        }
      }

      const res = await fetch(
        `/my-projects/${encodeURIComponent(project.id)}?user=${encodeURIComponent(
          userForDelete
        )}`,
        { method: "DELETE" }
      );

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Delete failed (${res.status})`);
      }

      setTotal((t) => Math.max(0, t - 1));

      // 🔄 Immediately trigger the same effect as clicking "Refresh"
      setRefreshKey((k) => k + 1);
    } catch (e) {
      // rollback
      setItems(prev);
      alert(e?.message || "Delete failed");
    }
  }


  function EmptyState() {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
          color: "#6b7280",
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          No projects yet
        </div>
        <div style={{ fontSize: 14 }}>
          Lets create some cool designs via one of the tabs above!
        </div>
      </div>
    );
  }

  const handleOpenReview = (project) => {
    setReviewProject(project);
  };

const handleAcceptReview = async () => {
  if (!reviewProject) return;

  try {
    // 1. Deduct 3 credits using /deduct-credit-custom/
    const res = await fetch("/deduct-credit-custom/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: username, // full email
      }),
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      alert(msg || "Failed to deduct credits.");
      return;
    }

    const data = await res.json();

    // 🔹 notify parent so TopBar can update
    if (data && typeof data.credits === "number") {
      onCreditsUpdate?.(data.credits);
    }

    // 2. Move the project folder using a new backend route
    let fsRoot = username;
    if (reviewProject.previewUrl) {
      const m = reviewProject.previewUrl.match(/^\/outputs\/([^/]+)\//);
      if (m && m[1]) fsRoot = m[1];
    }

    const moveRes = await fetch("/move-custom-project/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fromUser: fsRoot,
        toUser: username,
        folder: reviewProject.id,
      }),
    });

    if (!moveRes.ok) {
      const msg = await moveRes.text().catch(() => "");
      alert(msg || "Failed to move custom project folder.");
      return;
    }

    // Refresh list + close overlay
    setRefreshKey((k) => k + 1);
    setReviewProject(null);
  } catch (err) {
    alert(err?.message || "Something went wrong.");
  }
};



  const handleRejectReview = async () => {
    if (!reviewProject) return;
    // Reuse the same logic as the Delete button
    await handleDelete(reviewProject);
    // If deletion is cancelled in confirm, overlay stays open;
    // if successful, the item is gone and we close the overlay.
    setReviewProject(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          padding: 10,
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
          flexWrap: "wrap",
          position: "relative",
          zIndex: 20, // keep toolbar above any stray overlays
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 18,
            marginRight: 8,
            position: "relative",
            zIndex: 21,
          }}
        >
          My Projects
        </div>

        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search by filename…"
          style={{
            flex: 1,
            minWidth: 220,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            outline: "none",
            position: "relative",
            zIndex: 21,
          }}
        />

        <select
          value={kind}
          onChange={(e) => {
            const next = e.target.value;
            // Defer the state update so it wins over any parent/global click handlers
            setTimeout(() => {
              setKind(next);
              setPage(1);
            }, 0);
          }}
          title="Filter by type"
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            cursor: "pointer",
            position: "relative",
            zIndex: 21,
          }}
        >
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>

        <select
          value={sort}
          onChange={(e) => {
            const next = e.target.value;
            // Same trick: defer update to avoid being overwritten
            setTimeout(() => {
              setSort(next);
              setPage(1);
            }, 0);
          }}
          title="Sort"
          style={{
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            cursor: "pointer",
            position: "relative",
            zIndex: 21,
          }}
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            cursor: "pointer",
            position: "relative",
            zIndex: 21,
          }}
        >
          Refresh
        </button>
      </div>


      {/* Body */}
      <div style={{ padding: 12, flex: 1, overflow: "auto" }}>
        {error && (
          <div
            style={{
              marginBottom: 10,
              padding: 10,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            Loading…
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
          {items
            .filter((p) => {
              if (kind === "custom") return !!p.isCustom;
              return true; // all other kinds already filtered by backend
            })
            .map((p) => (
              <ProjectCard
                key={`${p.id}-${refreshKey}`}
                project={{ ...p, user: username }}
                onOpen={(proj) => onOpenProject?.(proj)}
                onDelete={handleDelete}
                onReview={handleOpenReview}
                refreshKey={refreshKey}
              />
            ))}

          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderTop: "1px solid #e5e7eb",
            background: "#fafafa",
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: page <= 1 ? "#e5e7eb" : "#ffffff",
              cursor: page <= 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>
          <div style={{ fontSize: 13, color: "#374151" }}>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: page >= totalPages ? "#e5e7eb" : "#ffffff",
              cursor: page >= totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Review overlay for custom requests */}
      {reviewProject && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: 16,
              padding: 20,
              width: "96vw",
              maxWidth: "900px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              boxShadow: "0 10px 40px rgba(15,23,42,0.35)",
              position: "relative",
            }}
          >
            {/* Close button */}
            <div
              onClick={() => setReviewProject(null)}
            style={{
              position: "absolute",
              top: 12,
              right: 12,

              fontSize: "3vh",   // scales X and box together
              width: "1.4em",
              height: "1.4em",
              padding: 0,
              boxSizing: "border-box",

              borderRadius: 8,
              background: "red",
              color: "#ffffff",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              cursor: "pointer",
              userSelect: "none",
              border: "0px solid #e2e8f0",
            }}
            >
              ×
            </div>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                Review Custom Request
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#4b5563",
                }}
              >
                {stripTimestampTag(
                  reviewProject.slug ||
                    reviewProject.name ||
                    reviewProject.id
                )}
              </div>
            </div>

            {/* Thumbnail preview (max-size square) */}
            {reviewProject.previewUrl && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  margin: "0px 0",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "min(88vw, 64vh)",
                    aspectRatio: "1 / 1",
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={reviewProject.previewUrl}
                    alt="Project preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Optional details if backend sent them */}
            {(reviewProject.customerEmail || reviewProject.customerNotes) && (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 8,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  fontSize: 13,
                  color: "#374151",
                }}
              >
                {reviewProject.customerEmail && (
                  <div style={{ marginBottom: 4 }}>
                    <strong>Customer Email:</strong>{" "}
                    {reviewProject.customerEmail}
                  </div>
                )}
                {reviewProject.customerNotes && (
                  <div>
                    <strong>Notes:</strong> {reviewProject.customerNotes}
                  </div>
                )}
              </div>
            )}

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Buttons at the bottom */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 12,
                marginTop: 10,
              }}
            >
              <button
                onClick={() => setShowCustomCreditOverlay(true)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #16a34a",
                  background: "#16a34a",
                  color: "white",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Accept
              </button>

              <button
                onClick={handleRejectReview}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #dc2626",
                  background: "#ffffff",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Custom credit confirmation overlay */}
      <DownloadOverlay
        visible={!!reviewProject && showCustomCreditOverlay}
        onClose={() => setShowCustomCreditOverlay(false)}
        onConfirm={async () => {
          // When user confirms: deduct 3 credits + move project
          await handleAcceptReview();
          setShowCustomCreditOverlay(false);
        }}
        username={null}        // null → DownloadOverlay skips /mark-downloaded/
        project={null}
        credits={3}            // 🔥 3-credit message
        extra={{ source: "custom_accept" }}
      />

    </div>
  );
}
